import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportFault, recentFaults } from "../lib/fault.js";

/**
 * Catches a render crash and shows it, instead of leaving a white rectangle.
 *
 * Before this, any throw during render unmounted the whole tree and React
 * logged to a console nobody has open on a phone. The player saw a blank screen
 * with no way back and no way to tell anyone what happened.
 *
 * Two escapes, in order of how much they cost the player: reload keeps the save
 * (most crashes are transient), and only if that fails do they need the reset,
 * which is why it is styled as the lesser option and asks first.
 */
interface State {
  error: Error | null;
}

export class FatalBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportFault("render", error, { fatal: true });
    if (info.componentStack) console.error("[desk] component stack:", info.componentStack);
  }

  private report(): string {
    const { error } = this.state;
    return [
      `DESK crash report`,
      `when: ${new Date().toISOString()}`,
      `where: render`,
      `error: ${error?.message ?? "unknown"}`,
      ``,
      error?.stack ?? "(no stack)",
      ``,
      `recent faults:`,
      ...recentFaults().map((f) => `  [${f.where}] ${f.message}`),
    ].join("\n");
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="fatal-screen" role="alert" data-testid="fatal-screen">
        <div className="fatal-card">
          <h1 className="fatal-title">The editor hit a snag</h1>
          <p className="fatal-lede">
            Something broke while drawing the screen. Your saved work is still on this device.
          </p>
          <pre className="fatal-detail" data-testid="fatal-detail">
            {error.message}
          </pre>
          <div className="fatal-actions">
            <button
              type="button"
              className="primary fatal-reload"
              data-testid="fatal-reload"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <button
              type="button"
              className="fatal-copy"
              data-testid="fatal-copy"
              onClick={() => {
                void navigator.clipboard?.writeText(this.report()).catch(() => undefined);
              }}
            >
              Copy details
            </button>
          </div>
          <button
            type="button"
            className="fatal-reset"
            data-testid="fatal-reset"
            onClick={() => {
              if (!window.confirm("Erase all saved progress on this device and start over?")) return;
              try {
                window.localStorage.clear();
              } catch {
                // Private mode / blocked storage: reloading is still worth a try.
              }
              window.location.href = window.location.pathname;
            }}
          >
            Still stuck? Erase saved progress and start over
          </button>
        </div>
      </div>
    );
  }
}
